/*
 *  (c) 2015 - 2023 ENISCO by FORCAM GmbH
 */

import React, {Fragment, useState} from 'react'
import {SearchApp, SearchGroup} from '../../../model/search'
import {Badge, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Typography} from '@mui/material'
import {DefaultGroupIcon} from '../../../icon/DefaultGroupIcon'
import {ExpandLess, ExpandMore} from '@mui/icons-material'
import {DefaultAppIcon} from '../../../icon/DefaultAppIcon'
import {clone, useThemeName} from '@enisco/mcc-react-lib'
import {HighlightedText} from './HighlightedText'

interface Props {
  handleClose: () => void
  result: SearchGroup
  searchKey: string
  currentApp?: SearchApp
  openApp: (app: SearchApp) => void
  onMouseOver: (app: SearchApp) => void
}

const SearchResultViewer: React.FC<Props> = ({
  result,
  searchKey,
  currentApp,
  openApp,
  onMouseOver
}) => {
  const [open, setOpen] = useState(true)
  const [themeName] = useThemeName()

  const handleClick = () => {
    setOpen(!open)
  }

  const sortedResult = clone(result.apps)
  sortedResult.sort((a, b) => (a.name || '').localeCompare(b.name || ''))

  const getBadge = (app: SearchApp) => {
    if (app.app) {
      return ''
    } else {
      if (app.favorite && app.favorite.user) {
        return themeName === 'dark'
          ? '/mcc-portal-ui/favorites/ic_favbadge_dark.svg'
          : '/mcc-portal-ui/favorites/ic_favbadge_light.svg'
      } else {
        return themeName === 'dark'
          ? '/mcc-portal-ui/favorites/ic_favbadge_global_dark.svg'
          : '/mcc-portal-ui/favorites/ic_favbadge_global_light.svg'
      }
    }
  }

  const currentSelection = (app: SearchApp): boolean | undefined => {
    if (
      currentApp?.app &&
      app.app?.module === currentApp?.app?.module &&
      app.app?.app === currentApp?.app?.app
    ) {
      return true
    }

    if (currentApp?.favorite && app.favorite?.id === currentApp?.favorite.id) {
      return true
    }

    return undefined
  }

  return (
    <Fragment>
      <ListItemButton onClick={() => handleClick()}>
        <ListItemIcon>
          {result.icon ? (
            <img src={result.icon} width={'32px'} height={'32px'} alt='' />
          ) : (
            <DefaultGroupIcon width={'32px'} height={'32px'} />
          )}
        </ListItemIcon>
        <ListItemText
          primary={<HighlightedText text={result.name} highlight={searchKey} />}
        />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout='auto' unmountOnExit>
        <List component='div' disablePadding>
          {sortedResult
            .filter((app) => !app.hidden)
            .map((app) => (
              <ListItemButton
                sx={[
                  {paddingLeft: 4},
                  currentSelection(app) ? {backgroundColor: 'action.hover'} : {}
                ]}
                onClick={() => openApp(app)}
                data-testid={`search-result-viewer-item-${app.name}`}
                key={app.name}
                onMouseOver={() => onMouseOver(app)}
              >
                <ListItemIcon>
                  <Badge
                    badgeContent={
                      <img src={getBadge(app)} width={'15px'} alt='' />
                    }
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                  >
                    {app.icon ? (
                      <img
                        src={app.icon}
                        width={'32px'}
                        height={'32px'}
                        alt=''
                      />
                    ) : (
                      <DefaultAppIcon width={'32px'} height={'32px'} />
                    )}
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant='body1'>
                      <HighlightedText text={app.name} highlight={searchKey} />
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
        </List>
      </Collapse>
    </Fragment>
  )
}

export default SearchResultViewer
